import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, Button, withStyles } from 'material-ui';
import { CircularProgress } from 'material-ui/Progress';
import { withApollo } from 'react-apollo';
import { withAlertHandler } from 'entoro-shared';
import { Field } from 'redux-form';
import { connect } from 'react-redux';
import '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { RESTAPI_URI, STRIPE_PUBLISHABLE_KEY } from '../../../../../config';
import {
  offerInvestmentsQuery,
  submitInvestmentMutation,
} from '../../../../../apollo';

const styles = () => ({
  container: {
    margin: '0 auto',
  },
  title: {
    fontSize: '16px',
    marginBottom: '10px',
  },
  subheading: {
    color: '#8c8c8c',
    fontWeight: '300',
    marginBottom: '10px',
  },
});

class PaymentMethod extends Component {
  state = {
    loading: false,
    token: null,
    showStripeForm: false,
  };

  componentDidMount() {
    const script = document.createElement('script');
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    script.onload = () => this.scriptLoaded();
  }

  handleStripeClick = async (event) => {
    event.preventDefault();
    this.setState({ loading: true });
    const { initialValues, investorUser, totalSteps, tenant, offer } =
      this.props;
    const stripe = await loadStripe(`${STRIPE_PUBLISHABLE_KEY}`);

    const { sessionId } = await fetch(
      `${RESTAPI_URI}/create-checkout-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currency: 'usd',
          paymentMethodType: 'card',
          offer,
          investmentAmount: initialValues.investmentAmount,
          investorUser,
          tenant,
          investmentId: initialValues._id,
          steps: totalSteps,
        }),
      }
    ).then(async (response) => await response.json());

    // redirect to checkout session screen
    await stripe.redirectToCheckout({ sessionId });
    // this.setState({ loading: false });
  };

  handlePlaidClick = async () => {
    const {
      handleSnackbarError,
      initialValues,
      investorUser,
      tenant,
      offer,
      handleClose,
    } = this.props;
    try {
      this.setState({ loading: true });
      const MySwal = withReactContent(Swal);

      const createLinkToken = async () => {
        const response = await fetch(`${RESTAPI_URI}/create_link_token`, {
          method: 'POST',
          body: JSON.stringify({
            investorUserId: investorUser._id,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const { linkToken } = await response.json();
        return linkToken;
      };

      const handler = window.Plaid.create({
        token: await createLinkToken(),
        onSuccess: async (publicToken, metadata) => {
          const data = await fetch(`${RESTAPI_URI}/set_access_token`, {
            method: 'POST',
            body: JSON.stringify({
              publicToken,
              accountid: metadata.accounts[0].id,
              stripeIssuerAccountId: offer.stripeAccountId,
              offer,
              investmentAmount: initialValues.investmentAmount,
              investorUser,
              tenant,
              investmentId: initialValues._id,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const { success, investmentId } = await data.json();

          if (success === 'pending') {
            // graphql mutation to update the payment status
            const { mutate } = this.props.client;
            const input = {
              _id: investmentId,
              offerId: offer._id,
              investmentStatus: 'PENDING_PAYMENT_VERIFICATION',
              paymentSent: true,
            };

            try {
              const refetchQueries = [
                {
                  query: offerInvestmentsQuery,
                  variables: {
                    offerId: offer._id,
                  },
                },
              ];

              await mutate({
                mutation: submitInvestmentMutation,
                variables: { input },
                refetchQueries,
              });
            } catch (error) {
              console.error('stored error', error);
            }
          }
        },
        onEvent: (metadata) => {
          if (metadata === 'HANDOFF') {
            handleClose();
            return MySwal.fire({
              title: 'Investment is pending!',
              text: ' Your investment will be approved after a review by an admin shortly',
              icon: 'info',
              confirmButtonColor: '#808080',
              allowOutsideClick: false,
              cancelButtonColor: '#d33',
              showCancelButton: false,
              confirmButtonText: 'Ok',
            });
          }
        },
        onExit: (error, metadata) => {
          handleClose();
          return MySwal.fire({
            title: 'Investment Failed',
            text: ' We noticed the investment is not complete, kindly review your investment details to continue',
            icon: 'info',
            confirmButtonColor: '#808080',
            allowOutsideClick: false,
            cancelButtonColor: '#d33',
            showCancelButton: false,
            confirmButtonText: `Ok`,
          });
        },
      });

      handler.open();
    } catch (error) {
      this.setState({ loading: false });
      handleSnackbarError(error);
    }
  };

  render() {
    const { classes, initialValues } = this.props;
    const { loading } = this.state;

    const paymentMethod = initialValues.paymentType === 'BANK_WIRE';
    const SignButton = ({ input: { value }, meta: { touched, error } }) => {
      const buttonText = `PAY VIA ${
        initialValues.paymentType === 'BANK_WIRE' ? 'Credit Card' : 'ACH'
      }`;

      return (
        <Fragment>
          <Typography align='center' className={classes.title}>
            <span style={touched && error ? { color: '#ff1744' } : null}>
              Online payments with{' '}
              {initialValues.paymentType === 'BANK_WIRE' ? 'Stripe' : 'Plaid'}
            </span>
          </Typography>

          <Typography align='center'>
            <Button
              disabled={loading}
              className='action-button'
              onClick={(e) =>
                paymentMethod
                  ? this.handleStripeClick(e)
                  : this.handlePlaidClick()
              }
              disableRipple
            >
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} color='inherit' />
                  &nbsp;
                  {buttonText}
                </div>
              ) : (
                buttonText
              )}
            </Button>
          </Typography>
        </Fragment>
      );
    };

    SignButton.propTypes = {
      input: PropTypes.object.isRequired,
      meta: PropTypes.object.isRequired,
    };

    return (
      <div className='step'>
        <Grid
          container
          spacing={16}
          justify='center'
          className={classes.container}
        >
          <Grid item xs={12} sm={6} md={4}>
            <Field name='paymentId' component={SignButton} />

            {/* <Field name='brokerageAgreementStatus' component={SignButton} /> */}
          </Grid>
        </Grid>
      </div>
    );
  }
}

PaymentMethod.propTypes = {
  classes: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  handleSnackbarError: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
};

export default connect()(
  withStyles(styles)(withApollo(withAlertHandler(PaymentMethod)))
);



// WEBPACK FOOTER //
// ./src/components/OfferOverview/ReadyToInvestButton/PaymentProcess/PaymentProcessForm/PaymentMethod.js